/**
 * The per-column filter popover.
 *
 * One component covering all five kinds, because a column's filter UI follows
 * from its type and a page should not have to choose. The text filter supports
 * **several conditions combined with AND**, which is what the server implements
 * — offering one condition where the backend takes a list would leave half the
 * capability unreachable.
 *
 * The select filter shows **counts from the server's facets**, cross-filtered by
 * whatever else is narrowed. That is the difference between a dropdown that
 * lists possibilities and one that tells you which of them will return
 * anything.
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Search, Trash2, X } from 'lucide-react';
import type { QueryLike } from '../../lib/query-state';

import { cn } from '../../lib/cn';
import { formatNumber, todayISO } from '../../lib/format';
import { Button } from '../ui/button';
import {
  STRING_MODE_LABELS,
  VALUELESS_MODES,
  emptyFilterFor,
  isActiveFilter,
  type ColumnFilterKind,
  type FacetOption,
  type FilterValue,
  type SelectOption,
  type StringCondition,
  type StringMode,
} from './types';
import { ScrollArea } from '../ui/scroll-area';

const MODE_ORDER: readonly StringMode[] = [
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts',
  'not_starts',
  'ends',
  'not_ends',
  'regex',
  'blank',
  'not_blank',
];

export interface ColumnFilterProps {
  kind: ColumnFilterKind;
  header: string;
  value: FilterValue | undefined;
  onChange: (value: FilterValue | undefined) => void;
  onClose: () => void;
  /** Declared options for a select filter. */
  options?: readonly SelectOption[];
  /** The server's facet counts, as their request stands — loaded when the
   * popover opens, not before. Whole, so a failure is told apart from "no
   * values" (audit 35, U1). */
  facets?: FacetsRequest;
}

/** The facet request's state, as far as the filter draws it. */
export type FacetsRequest = QueryLike<FacetOption[]> & { isLoading: boolean };

export function ColumnFilter({
  kind,
  header,
  value,
  onChange,
  onClose,
  options,
  facets,
}: ColumnFilterProps) {
  // Edited locally and applied on Apply, so a half-typed condition never fires
  // a request — and Cancel genuinely cancels.
  const [draft, setDraft] = useState<FilterValue>(value ?? emptyFilterFor(kind));

  useEffect(() => {
    setDraft(value ?? emptyFilterFor(kind));
  }, [value, kind]);

  const apply = () => {
    onChange(isActiveFilter(draft) ? draft : undefined);
    onClose();
  };

  const clear = () => {
    onChange(undefined);
    onClose();
  };

  return (
    <div className="w-72 p-3">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <p className="truncate text-xs font-semibold text-heading">{header}</p>
        <button
          type="button"
          onClick={onClose}
          className="-m-1 rounded p-1 text-faint hover:text-body"
          aria-label="Close filter"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {draft.type === 'string' ? (
        <TextFilter value={draft} onChange={setDraft} />
      ) : null}
      {draft.type === 'select' ? (
        <SelectFilter
          value={draft}
          onChange={setDraft}
          options={options}
          facets={facets}
        />
      ) : null}
      {draft.type === 'boolean' ? <BooleanFilter value={draft} onChange={setDraft} /> : null}
      {draft.type === 'number-range' ? <NumberFilter value={draft} onChange={setDraft} /> : null}
      {draft.type === 'date-range' ? <DateFilter value={draft} onChange={setDraft} /> : null}

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-line pt-2.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={clear}
          disabled={!value}
          className="text-xs text-muted"
        >
          <Trash2 className="size-3.5" />
          Clear
        </Button>
        <Button size="sm" onClick={apply}>
          Apply
        </Button>
      </div>
    </div>
  );
}

// ── text ────────────────────────────────────────────────────
function TextFilter({
  value,
  onChange,
}: {
  value: Extract<FilterValue, { type: 'string' }>;
  onChange: (value: FilterValue) => void;
}) {
  const setCondition = (index: number, patch: Partial<StringCondition>) => {
    const conditions = value.conditions.map((condition, i) =>
      i === index ? { ...condition, ...patch } : condition,
    );
    onChange({ type: 'string', conditions });
  };

  const addCondition = () => {
    onChange({
      type: 'string',
      conditions: [...value.conditions, { mode: 'contains', value: '' }],
    });
  };

  const removeCondition = (index: number) => {
    const conditions = value.conditions.filter((_, i) => i !== index);
    onChange({
      type: 'string',
      // Never zero conditions: an empty list renders nothing and the popover
      // looks broken. One empty condition is the resting state.
      conditions: conditions.length > 0 ? conditions : [{ mode: 'contains', value: '' }],
    });
  };

  return (
    <div className="space-y-2">
      {value.conditions.map((condition, index) => {
        const valueless = VALUELESS_MODES.includes(condition.mode);
        return (
          <div key={index} className="space-y-1.5">
            {index > 0 ? (
              // Says AND out loud. Two conditions with nothing between them
              // could mean either, and the server means AND.
              <p className="text-2xs font-medium uppercase tracking-wide text-faint">and</p>
            ) : null}
            <div className="flex gap-1.5">
              <select
                value={condition.mode}
                onChange={(event) =>
                  setCondition(index, { mode: event.target.value as StringMode })
                }
                className="h-8 min-w-0 flex-1 cursor-pointer rounded-control border border-line bg-surface px-1.5 text-xs text-body"
                aria-label="Condition"
              >
                {MODE_ORDER.map((mode) => (
                  <option key={mode} value={mode}>
                    {STRING_MODE_LABELS[mode]}
                  </option>
                ))}
              </select>
              {value.conditions.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeCondition(index)}
                  className="shrink-0 rounded p-1.5 text-faint hover:bg-surface-2 hover:text-danger"
                  aria-label="Remove condition"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </div>
            {!valueless ? (
              <input
                value={condition.value}
                onChange={(event) => setCondition(index, { value: event.target.value })}
                placeholder={condition.mode === 'regex' ? 'Regular expression' : 'Value'}
                className="h-8 w-full rounded-control border border-line bg-surface px-2 text-xs text-body placeholder:text-faint focus:border-accent focus:outline-none"
              />
            ) : null}
          </div>
        );
      })}

      {/* Capped at three. Beyond that a column filter is a query, and the
          register's search or a saved view is the better tool. */}
      {value.conditions.length < 3 ? (
        <button
          type="button"
          onClick={addCondition}
          className="flex items-center gap-1 text-2xs font-medium text-accent hover:underline"
        >
          <Plus className="size-3" />
          Add condition
        </button>
      ) : null}
    </div>
  );
}

// ── select ──────────────────────────────────────────────────
function SelectFilter({
  value,
  onChange,
  options,
  facets,
}: {
  value: Extract<FilterValue, { type: 'select' }>;
  onChange: (value: FilterValue) => void;
  options?: readonly SelectOption[];
  facets?: FacetsRequest;
}) {
  const [search, setSearch] = useState('');

  /**
   * The options to show.
   *
   * Declared options win on labels — the server's facets carry raw values, and
   * `on_hold` should read as "On hold". Facets supply the counts and any value
   * the declaration did not anticipate, so a status added on the server appears
   * here without a client change.
   */
  const merged = useMemo(() => {
    const counts = new Map((facets?.data ?? []).map((facet) => [facet.value, facet.count]));
    const declared = (options ?? []).map((option) => ({
      value: option.value,
      label: option.label,
      count: counts.get(option.value),
    }));
    const declaredValues = new Set(declared.map((option) => option.value));
    const extras = (facets?.data ?? [])
      .filter((facet) => !declaredValues.has(facet.value))
      .map((facet) => ({
        value: facet.value,
        label: facet.value.replace(/[_-]+/g, ' '),
        count: facet.count,
      }));
    // Anything currently selected is kept visible even if the facets no longer
    // return it — otherwise ticking a value can make it vanish from its own list.
    const selectedExtras = value.values
      .filter((v) => !declaredValues.has(v) && !extras.some((e) => e.value === v))
      .map((v) => ({ value: v, label: v, count: 0 }));
    return [...declared, ...extras, ...selectedExtras];
  }, [options, facets, value.values]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return merged;
    return merged.filter((option) => option.label.toLowerCase().includes(needle));
  }, [merged, search]);

  const toggle = (optionValue: string) => {
    const values = value.values.includes(optionValue)
      ? value.values.filter((v) => v !== optionValue)
      : [...value.values, optionValue];
    onChange({ type: 'select', values });
  };

  return (
    <div className="space-y-2">
      {merged.length > 8 ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find an option"
            className="h-8 w-full rounded-control border border-line bg-surface pl-7 pr-2 text-xs text-body placeholder:text-faint focus:border-accent focus:outline-none"
          />
        </div>
      ) : null}

      <ScrollArea className="max-h-56" contentClassName="space-y-px">
        {facets?.isError && facets.data === undefined ? (
          // The declared options still filter; what is missing is the counts,
          // and — with nothing declared — the values themselves.
          <p className="px-1 py-2 text-xs text-danger">
            {merged.length > 0 ? 'The counts could not be read.' : 'The values could not be read.'}{' '}
            <button type="button" className="underline" onClick={() => void facets.refetch()}>
              Try again
            </button>
          </p>
        ) : null}
        {facets?.isLoading ? (
          <p className="px-1 py-2 text-xs text-faint">Counting…</p>
        ) : visible.length === 0 ? (
          facets?.isError ? null : (
            <p className="px-1 py-2 text-xs text-faint">
              {search ? 'No option matches that.' : 'No values to filter by.'}
            </p>
          )
        ) : (
          visible.map((option) => {
            const checked = value.values.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => toggle(option.value)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-1.5 py-1.5 text-left text-xs transition-colors',
                  checked ? 'bg-accent-subtle text-accent' : 'text-body hover:bg-surface-2',
                )}
              >
                <span
                  className={cn(
                    'flex size-3.5 shrink-0 items-center justify-center rounded border',
                    checked ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong',
                  )}
                  aria-hidden
                >
                  {checked ? <Check className="size-2.5" /> : null}
                </span>
                <span className="flex-1 truncate capitalize">{option.label}</span>
                {option.count !== undefined ? (
                  <span className="shrink-0 text-2xs text-faint tabular">
                    {formatNumber(option.count)}
                  </span>
                ) : null}
              </button>
            );
          })
        )}
      </ScrollArea>

      {value.values.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange({ type: 'select', values: [] })}
          className="text-2xs font-medium text-accent hover:underline"
        >
          Deselect all ({value.values.length})
        </button>
      ) : null}
    </div>
  );
}

// ── boolean ─────────────────────────────────────────────────
function BooleanFilter({
  value,
  onChange,
}: {
  value: Extract<FilterValue, { type: 'boolean' }>;
  onChange: (value: FilterValue) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {[
        { label: 'Yes', on: true },
        { label: 'No', on: false },
      ].map((choice) => (
        <button
          key={choice.label}
          type="button"
          onClick={() => onChange({ type: 'boolean', value: choice.on })}
          className={cn(
            'flex-1 rounded-control border px-2 py-1.5 text-xs font-medium transition-colors',
            value.value === choice.on
              ? 'border-accent bg-accent-subtle text-accent'
              : 'border-line text-muted hover:bg-surface-2',
          )}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

// ── number ──────────────────────────────────────────────────
function NumberFilter({
  value,
  onChange,
}: {
  value: Extract<FilterValue, { type: 'number-range' }>;
  onChange: (value: FilterValue) => void;
}) {
  const set = (key: 'min' | 'max', raw: string) => {
    const parsed = raw.trim() === '' ? undefined : Number(raw);
    // An unparseable entry leaves the bound alone rather than writing NaN,
    // which would serialize as null and silently drop the bound.
    onChange({
      ...value,
      [key]: parsed === undefined || Number.isNaN(parsed) ? undefined : parsed,
    });
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={value.min ?? ''}
        onChange={(event) => set('min', event.target.value)}
        placeholder="Min"
        className="h-8 w-full rounded-control border border-line bg-surface px-2 text-xs text-body placeholder:text-faint focus:border-accent focus:outline-none"
      />
      <span className="text-xs text-faint">to</span>
      <input
        type="number"
        value={value.max ?? ''}
        onChange={(event) => set('max', event.target.value)}
        placeholder="Max"
        className="h-8 w-full rounded-control border border-line bg-surface px-2 text-xs text-body placeholder:text-faint focus:border-accent focus:outline-none"
      />
    </div>
  );
}

// ── date ────────────────────────────────────────────────────
const DATE_PRESETS: readonly { label: string; days: number }[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

function DateFilter({
  value,
  onChange,
}: {
  value: Extract<FilterValue, { type: 'date-range' }>;
  onChange: (value: FilterValue) => void;
}) {
  const applyPreset = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    onChange({
      type: 'date-range',
      // `todayISO`, not `toISOString`: the latter converts to UTC, so east of
      // Greenwich a "last 7 days" preset ends YESTERDAY until the offset has
      // passed — and today's rows are missing from a filter that says it
      // includes them.
      from: todayISO(from),
      to: todayISO(to),
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={value.from ?? ''}
          onChange={(event) => onChange({ ...value, from: event.target.value || undefined })}
          className="h-8 w-full rounded-control border border-line bg-surface px-2 text-xs text-body focus:border-accent focus:outline-none"
          aria-label="From"
        />
        <span className="text-xs text-faint">to</span>
        <input
          type="date"
          value={value.to ?? ''}
          onChange={(event) => onChange({ ...value, to: event.target.value || undefined })}
          className="h-8 w-full rounded-control border border-line bg-surface px-2 text-xs text-body focus:border-accent focus:outline-none"
          aria-label="To"
        />
      </div>
      {/* The presets are most of the actual usage — "last 30 days" is what
          somebody wants, and making them pick two dates for it is friction. */}
      <div className="flex flex-wrap gap-1">
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => applyPreset(preset.days)}
            className="rounded-pill border border-line px-2 py-0.5 text-2xs text-muted transition-colors hover:border-accent/40 hover:bg-accent-subtle hover:text-accent"
          >
            {preset.label}
          </button>
        ))}
      </div>
      <p className="text-2xs leading-relaxed text-faint">
        A date here covers the whole day — “to” includes everything recorded on that date.
      </p>
    </div>
  );
}
